import type { AtomRecord } from "../model/atom.js";
import type { CellRecord } from "../model/cell.js";
import {
  type AttentionVector,
  type BoardPhase,
  type PathCandidateView,
  type PositionEvaluation,
  type ProofDebtView,
  type ThreatView,
} from "../model/evaluation.js";
import type { BoardState } from "../model/position.js";
import { derivePathCandidates } from "./path-candidates.js";

export type EvaluationDetail = {
  attentionAtoms: AtomRecord[];
  attentionCells: CellRecord[];
  candidateLines: PathCandidateView[];
  evaluation: PositionEvaluation;
  mainline: AtomRecord[];
  proofs: ProofDebtView[];
  threats: ThreatView[];
};

const MAX_FOCUS_ATOMS = 8;
const MAX_FOCUS_CELLS = 16;

export function evaluateBoardState(state: BoardState): EvaluationDetail {
  const atoms = Object.values(state.atoms).filter((atom) => atom.status !== "archived");
  const cells = Object.values(state.cells).filter((cell) => cell.status === "active");
  const candidateLines = derivePathCandidates(state);
  const mainline = deriveMainline(state, atoms, candidateLines);
  const threats = deriveThreats(state, mainline);
  const proofs = deriveProofDebt(state, mainline, threats);
  const attentionAtoms = uniqueAtoms([
    ...mainline,
    ...threats
      .map((threat) => [state.atoms[threat.sourceAtomId], state.atoms[threat.targetAtomId]])
      .flat()
      .filter((atom): atom is AtomRecord => atom !== undefined),
    ...proofs
      .map((proof) => [state.atoms[proof.targetAtomId], proof.threatAtomId === undefined
        ? undefined
        : state.atoms[proof.threatAtomId]])
      .flat()
      .filter((atom): atom is AtomRecord => atom !== undefined),
  ]).slice(0, MAX_FOCUS_ATOMS);
  const attentionAtomIds = new Set(attentionAtoms.map((atom) => atom.id));
  const attentionCells = cells
    .filter((cell) => attentionAtomIds.has(cell.from) || attentionAtomIds.has(cell.to))
    .slice(0, MAX_FOCUS_CELLS);

  const attentionVector = deriveAttentionVector(state, mainline, threats, proofs);
  const phase = derivePhase(state, mainline, attentionVector);

  return {
    attentionAtoms,
    attentionCells,
    candidateLines,
    evaluation: {
      attentionVector,
      phase,
      summary: summarizeEvaluation(phase, attentionVector, candidateLines, mainline, threats, proofs),
    },
    mainline,
    proofs,
    threats,
  };
}

function deriveMainline(
  state: BoardState,
  atoms: AtomRecord[],
  candidateLines: PathCandidateView[],
) {
  const selected = state.line.selectedPathId;
  if (selected !== undefined) {
    const atom = state.atoms[selected];
    if (atom?.kind === "path" && atom.status !== "archived") {
      return [atom];
    }
  }

  return atoms
    .filter((atom) => atom.id === candidateLines[0]?.pathId)
    .slice(0, 1);
}

function isRequiredAtom(atom: AtomRecord) {
  return (
    atom.status !== "archived" &&
    atom.status !== "rejected" &&
    (atom.kind === "goal" || atom.kind === "constraint")
  );
}

function deriveRequiredTargets(state: BoardState, mainline: AtomRecord[]) {
  return uniqueAtoms([
    ...mainline,
    ...Object.values(state.atoms)
      .filter(isRequiredAtom)
      .sort((left, right) => left.id.localeCompare(right.id)),
  ]);
}

function deriveResolvedProofTargets(state: BoardState) {
  return new Set(
    Object.values(state.cells)
      .filter((cell) => cell.status === "active" && cell.effect === "proves")
      .filter((cell) => {
        const source = state.atoms[cell.from];
        return source?.kind === "proof" && source.status === "resolved";
      })
      .map((cell) => cell.to),
  );
}

function deriveThreats(state: BoardState, mainline: AtomRecord[]): ThreatView[] {
  const focusTargets = new Set<string>();
  for (const atom of mainline) {
    focusTargets.add(atom.id);
  }

  for (const cell of Object.values(state.cells)) {
    if (cell.status !== "active" || cell.effect !== "supports") {
      continue;
    }
    if (!focusTargets.has(cell.from)) {
      continue;
    }
    focusTargets.add(cell.to);
  }

  return Object.values(state.cells)
    .filter((cell) => cell.status === "active" && cell.effect === "threatens")
    .filter((cell) => focusTargets.has(cell.to))
    .filter((cell) => {
      const source = state.atoms[cell.from];
      return source !== undefined && source.status === "active";
    })
    .map((cell) => ({
      cellId: cell.id,
      sourceAtomId: cell.from,
      summary: `${cell.from} threatens ${cell.to}`,
      targetAtomId: cell.to,
    }));
}

function deriveProofDebt(
  state: BoardState,
  mainline: AtomRecord[],
  threats: ThreatView[],
) {
  const debts: ProofDebtView[] = [];
  const debtKeys = new Set<string>();
  const provedTargets = deriveResolvedProofTargets(state);

  const addDebt = (debt: ProofDebtView) => {
    const key = `${debt.targetAtomId}:${debt.threatAtomId ?? ""}`;
    if (debtKeys.has(key)) {
      return;
    }
    debtKeys.add(key);
    debts.push(debt);
  };

  for (const threat of threats) {
    if (!provedTargets.has(threat.sourceAtomId) && !provedTargets.has(threat.targetAtomId)) {
      addDebt({
        summary: `Need proof for ${threat.sourceAtomId} before trusting ${threat.targetAtomId}`,
        targetAtomId: threat.targetAtomId,
        threatAtomId: threat.sourceAtomId,
      });
    }
  }

  for (const atom of deriveRequiredTargets(state, mainline)) {
    if (provedTargets.has(atom.id)) {
      continue;
    }

    addDebt({
      summary:
        atom.kind === "path"
          ? `Selected path ${atom.id} has no proof yet`
          : `${atom.kind} ${atom.id} has no proof yet`,
      targetAtomId: atom.id,
    });
  }

  return debts;
}

function deriveAttentionVector(
  state: BoardState,
  mainline: AtomRecord[],
  threats: ThreatView[],
  proofs: ProofDebtView[],
): AttentionVector {
  const activePaths = Object.values(state.atoms).filter(
    (atom) => atom.kind === "path" && atom.status !== "archived",
  );
  const danglingAtoms = Object.values(state.atoms).filter((atom) => {
    if (atom.status === "archived") {
      return false;
    }
    return !Object.values(state.cells).some(
      (cell) => cell.status === "active" && (cell.from === atom.id || cell.to === atom.id),
    );
  });

  return {
    entropy: Math.max(activePaths.length - Math.max(mainline.length, 1), 0) + danglingAtoms.length,
    forcingThreats: threats.length,
    lineDebt: deriveLineDebt(state, mainline),
    proofDebt: proofs.length,
  };
}

function deriveLineDebt(state: BoardState, mainline: AtomRecord[]) {
  if (state.line.selectedPathId === undefined) {
    return 2;
  }
  if (mainline.length === 0) {
    return 2;
  }

  const selectedPathId = mainline[0]?.id;
  if (selectedPathId === undefined) {
    return 2;
  }

  let debt = 0;
  const supportedGoalIds = new Set(
    Object.values(state.cells)
      .filter((cell) => cell.status === "active" && cell.effect === "supports" && cell.from === selectedPathId)
      .map((cell) => cell.to),
  );
  const activeGoals = Object.values(state.atoms).filter(
    (atom) => atom.kind === "goal" && atom.status !== "archived" && atom.status !== "rejected",
  );
  const usesTool = Object.values(state.cells).some(
    (cell) => cell.status === "active" && cell.effect === "uses" && cell.from === selectedPathId,
  );

  for (const goal of activeGoals) {
    if (!supportedGoalIds.has(goal.id)) {
      debt += 1;
    }
  }
  if (!usesTool) {
    debt += 1;
  }
  return debt;
}

function derivePhase(
  state: BoardState,
  mainline: AtomRecord[],
  attentionVector: AttentionVector,
): BoardPhase {
  const hasProblem = Object.values(state.atoms).some((atom) => atom.kind === "problem");
  const hasGoal = Object.values(state.atoms).some((atom) => atom.kind === "goal");

  if (!hasProblem || !hasGoal) {
    return "empty";
  }
  if (mainline.length === 0) {
    return "framing";
  }
  if (attentionVector.forcingThreats > 0) {
    return "contested";
  }
  if (attentionVector.lineDebt > 0) {
    return "framing";
  }
  if (attentionVector.proofDebt > 0) {
    return "ready";
  }
  return "converged";
}

function summarizeEvaluation(
  phase: BoardPhase,
  attentionVector: AttentionVector,
  candidateLines: PathCandidateView[],
  mainline: AtomRecord[],
  threats: ThreatView[],
  proofs: ProofDebtView[],
) {
  const selected = mainline[0]?.id ?? "none";
  return [
    `phase=${phase}`,
    `line=${selected}`,
    `candidates=${candidateLines.length}`,
    `threats=${threats.length}`,
    `proofs=${proofs.length}`,
    `lineDebt=${attentionVector.lineDebt}`,
    `entropy=${attentionVector.entropy}`,
  ].join(" ");
}

function uniqueAtoms(atoms: AtomRecord[]) {
  const seen = new Set<string>();
  const unique: AtomRecord[] = [];
  for (const atom of atoms) {
    if (seen.has(atom.id)) {
      continue;
    }
    seen.add(atom.id);
    unique.push(atom);
  }
  return unique;
}
